import useInfiniteScroll from "react-infinite-scroll-hook";
import { useIntl } from "react-intl";

import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { OrderCardWithoutReservation } from "@/features/bookings/components/OrderCardWithoutReservation";
import { OrdersInCreationCard } from "@/features/bookings/components/OrdersInCreationCard";
import { useReservations } from "@/features/bookings/hooks/useReservations";
import { ReservationType } from "@/features/bookings/types/types";
import { BookedHotelCard } from "@/features/hotels/components/hotels-list/BookedHotelCard";
import { formatCurrency } from "@/utils/currency";

export const MyHotelsList = ({
  onReservationClick,
  reservationType,
  reservationTypeSwitcher,
}: {
  onReservationClick: (reservationId: string) => void;
  reservationType: ReservationType;
  reservationTypeSwitcher: React.ReactNode;
}) => {
  const intl = useIntl();
  const { data, isLoading, isFetching, isError, hasReachedEnd, fetchNextPage } =
    useReservations({ searchFilter: reservationType, service: "hotel" });
  const [sentryRef] = useInfiniteScroll({
    loading: isFetching,
    hasNextPage: !hasReachedEnd,
    onLoadMore: () => {
      fetchNextPage();
    },
    disabled: isError,
    rootMargin: "0px 0px 40px 0px",
  });

  if (isLoading) {
    return (
      <PageLoader>
        <AnimatedLoader icon="buildings" />
      </PageLoader>
    );
  }

  const bookingCount = data?.pages?.[0]?.data?.count;
  const bookings =
    data?.pages?.flatMap((page) => page.data.hotelReservations) || [];
  const inProgressReservationsCount =
    data?.pages?.[0]?.data?.inProgressReservationsCount;

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        {reservationTypeSwitcher}
        {bookingCount ? (
          <div className="text-md font-medium">
            {intl.formatMessage(
              {
                id: "hotels.bookedHotels",
                defaultMessage:
                  "{bookingCount, plural, one {# booking} other {# bookings}}",
              },
              { bookingCount },
            )}
          </div>
        ) : null}
      </div>

      {inProgressReservationsCount && inProgressReservationsCount > 0 ? (
        <OrdersInCreationCard count={inProgressReservationsCount} />
      ) : null}

      {bookings.length > 0 ? (
        <ul
          aria-label={intl.formatMessage({
            id: "hotels.bookedHotelsList",
            defaultMessage: "Booked hotels list",
          })}
          className="space-y-2"
        >
          {bookings.map((reservation) => (
            <li key={reservation.id}>
              {reservation.status === ReservationType.REFUNDED ? (
                <OrderCardWithoutReservation
                  reservation={reservation}
                  onClick={() => onReservationClick(reservation.id)}
                />
              ) : (
                <BookedHotelCard
                  reservation={reservation.externalReservation}
                  onClick={() => onReservationClick(reservation.id)}
                  footer={
                    <div className="text-end text-sm">
                      <span className="font-semibold text-text-primary">
                        {formatCurrency(
                          reservation.price,
                          reservation.currency,
                        )}
                      </span>
                    </div>
                  }
                />
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {isFetching || !hasReachedEnd ? (
        <div
          className="flex items-center justify-center py-4"
          ref={sentryRef}
          aria-hidden={!isFetching}
        >
          <AnimatedLoader icon="buildings" />
        </div>
      ) : (
        <div className="flex items-center justify-center p-8 text-text-tertiary">
          {intl.formatMessage({
            id: "hotels.noMoreResults",
            defaultMessage: "No more results",
          })}
        </div>
      )}
    </>
  );
};
